import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { equipaggiamentoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ImageLightbox } from '../components/ImageLightbox';
import { HardHat, Plus, Trash2, Loader2, Pencil, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE_EQUIPAGGIAMENTO = [
  'Casco',
  'Giacca',
  'Pantaloni',
  'Guanti',
  'Stivali',
  'Protezioni',
  'Tuta',
  'Antipioggia',
  'Borse',
  'Accessori',
  'Altro'
];

const VALUTE = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

export default function Equipaggiamento() {
  const [equipaggiamenti, setEquipaggiamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({
    nome: '',
    categoria: '',
    marca: '',
    modello: '',
    data_acquisto: '',
    prezzo: null,
    valuta: 'CHF',
    foto_urls: [],
    note: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await equipaggiamentoApi.getAll();
      setEquipaggiamenti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      categoria: '',
      marca: '',
      modello: '',
      data_acquisto: '',
      prezzo: null,
      valuta: 'CHF',
      foto_urls: [],
      note: ''
    });
    setEditingId(null);
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const handleOpenDialog = (equip = null) => {
    if (equip) {
      setFormData({
        nome: equip.nome,
        categoria: equip.categoria,
        marca: equip.marca || '',
        modello: equip.modello || '',
        data_acquisto: equip.data_acquisto || '',
        prezzo: equip.prezzo,
        valuta: equip.valuta || 'EUR',
        foto_urls: equip.foto_urls || [],
        note: equip.note || ''
      });
      setEditingId(equip.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.categoria) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await equipaggiamentoApi.update(editingId, formData);
        toast.success('Equipaggiamento aggiornato');
      } else {
        await equipaggiamentoApi.create(formData);
        toast.success('Equipaggiamento aggiunto');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo equipaggiamento?')) return;
    
    try {
      await equipaggiamentoApi.delete(id);
      toast.success('Equipaggiamento eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatCurrency = (value, valuta = 'EUR') => {
    if (!value) return '—';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: valuta
    }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="equipaggiamento-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Equipaggiamento</h1>
            <p className="page-subtitle">Il tuo gear da moto</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                data-testid="add-equip-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                  {editingId ? 'Modifica Equipaggiamento' : 'Nuovo Equipaggiamento'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="form-label">Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="es. Casco AGV Pista GP RR"
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="equip-nome"
                  />
                </div>

                <div>
                  <Label className="form-label">Categoria *</Label>
                  <Select 
                    value={formData.categoria} 
                    onValueChange={(v) => setFormData({...formData, categoria: v})}
                  >
                    <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="equip-categoria-select">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                      {CATEGORIE_EQUIPAGGIAMENTO.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Marca</Label>
                    <Input
                      value={formData.marca}
                      onChange={(e) => setFormData({...formData, marca: e.target.value})}
                      placeholder="es. AGV"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="equip-marca"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Modello</Label>
                    <Input
                      value={formData.modello}
                      onChange={(e) => setFormData({...formData, modello: e.target.value})}
                      placeholder="es. Pista GP RR"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="equip-modello"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Data Acquisto</Label>
                    <Input
                      value={formData.data_acquisto}
                      onChange={(e) => setFormData({...formData, data_acquisto: e.target.value})}
                      placeholder="GG.MM.AAAA"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="equip-data"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Valuta</Label>
                    <Select 
                      value={formData.valuta} 
                      onValueChange={(v) => setFormData({...formData, valuta: v})}
                    >
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="equip-valuta-select">
                        <SelectValue placeholder="Valuta" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        {VALUTE.map(v => (
                          <SelectItem key={v.code} value={v.code}>
                            {v.symbol} {v.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="form-label">Prezzo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.prezzo || ''}
                    onChange={(e) => setFormData({...formData, prezzo: e.target.value ? parseFloat(e.target.value) : null})}
                    onFocus={(e) => e.target.select()}
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                    data-testid="equip-prezzo"
                  />
                </div>

                {/* Foto Upload */}
                <div>
                  <Label className="form-label">Foto</Label>
                  <FotoUploader
                    fotoUrls={formData.foto_urls}
                    setFotoUrls={(urls) => {
                      if (typeof urls === 'function') {
                        setFormData(prev => ({...prev, foto_urls: urls(prev.foto_urls)}));
                      } else {
                        setFormData({...formData, foto_urls: urls});
                      }
                    }}
                    maxPhotos={5}
                  />
                </div>

                <div>
                  <Label className="form-label">Note</Label>
                  <Input
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="equip-note"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                    data-testid="equip-submit"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingId ? 'Aggiorna' : 'Aggiungi')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {equipaggiamenti.length === 0 ? (
          <div className="vault-card text-center py-16">
            <HardHat className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Nessun equipaggiamento</h3>
            <p className="text-[#9CA3AF] mb-6">Aggiungi il tuo gear da moto</p>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aggiungi Equipaggiamento
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {equipaggiamenti.map((equip) => (
              <div
                key={equip.id}
                className="vault-card"
                data-testid={`equip-item-${equip.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#FF5F1F]/10 flex items-center justify-center">
                    <HardHat className="w-5 h-5 text-[#FF5F1F]" />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenDialog(equip)}
                      className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                      data-testid={`edit-equip-${equip.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(equip.id)}
                      className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                      data-testid={`delete-equip-${equip.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <span className="vault-badge vault-badge-orange mb-2">{equip.categoria}</span>
                <h3 className="text-white font-medium text-lg mt-2">{equip.nome}</h3>
                
                {(equip.marca || equip.modello) && (
                  <p className="text-[#9CA3AF] text-sm mt-1">
                    {equip.marca} {equip.modello}
                  </p>
                )}

                {equip.foto_urls && equip.foto_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-1 mt-3">
                    {equip.foto_urls.slice(0, 3).map((url, idx) => (
                      <div 
                        key={idx} 
                        className="relative cursor-pointer group/img"
                        onClick={() => openLightbox(equip.foto_urls, idx)}
                      >
                        <img 
                          src={getImageUrl(url)} 
                          alt="" 
                          className="w-full h-12 object-cover border border-[#262626]" 
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#262626]">
                  <span className="text-[#FF5F1F] font-mono">{formatCurrency(equip.prezzo, equip.valuta || 'EUR')}</span>
                  {equip.data_acquisto && (
                    <span className="text-[#9CA3AF] font-mono text-sm">{equip.data_acquisto}</span>
                  )}
                </div>

                {equip.note && (
                  <p className="text-[#9CA3AF] text-sm mt-3 line-clamp-2">{equip.note}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
